import React, { ChangeEvent, DragEvent } from "react";
import { Link } from "react-router-dom";

import { V2 } from "../../../Api";
import { RouteComponentPropsHistory } from "../../../types";
import { formatDateLocalNumeric } from "../../../Utilities";
import { IAuthProps } from "../../AuthContext";
import DeletionConfirmationDialog from "../../DeletionConfirmationDialog";
import FileUploadDialog from "../../FileUploadDialog";
import ToolbarMenuItem from "../../ToolbarMenuItem";
import ViewToolbar from "../../ViewToolbar";
import withAuth from "../../withAuth";

import withTitle from "../../withTitle";

interface IDocumentSelectionViewProps extends RouteComponentPropsHistory<any, any>, IAuthProps {
}

interface IDocumentSelectionViewState {
    isLoading: boolean | string;
    isUploadDialogOpen: boolean;
    documentTypes: any[];
    allDocumentTypes: any[];
    uploadDocumentType: string;
    patientId: string;
    isDeleteDialogOpen: boolean;

    uploadDragCounter: number;
    uploadFilesOverride: FileList | undefined;

    templates: any[];

    deleteSelected: any | null;
}

class DocumentSelectionView extends React.Component<IDocumentSelectionViewProps, IDocumentSelectionViewState> {
    constructor(props: Readonly<IDocumentSelectionViewProps>) {
        super(props);

        const state: IDocumentSelectionViewState = {
            isLoading: true,
            isUploadDialogOpen: false,
            documentTypes: [],
            allDocumentTypes: [],
            uploadDocumentType: "",
            patientId: this.props.match.params.patient_id,
            isDeleteDialogOpen: false,

            uploadDragCounter: 0,
            uploadFilesOverride: undefined,

            templates: [],

            deleteSelected: null
        };

        this.state = state;

        this.toggleUploadDialog = this.toggleUploadDialog.bind(this);
        this.uploadSuccess = this.uploadSuccess.bind(this);
        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.handleDragEnter = this.handleDragEnter.bind(this);
        this.handleDragLeave = this.handleDragLeave.bind(this);
        this.handleDragDrop = this.handleDragDrop.bind(this);
        this.toggleDeleteDialog = this.toggleDeleteDialog.bind(this);
        this.deleteTemplate = this.deleteTemplate.bind(this);
    }

    public componentDidMount() {
        if (this.props.location.state?.action === "select_template") {
            this.loadTemplates(this.props.location.state?.data.id);
        }
        else {
            this.loadData();
        }
    }

    public componentDidUpdate(prevProps: Readonly<IDocumentSelectionViewProps>) {
        if (this.props.location.state?.action === "select_template" && prevProps.location.state?.action !== this.props.location.state?.action) {
            this.loadTemplates(this.props.location.state?.data.id);
        }
        else if (prevProps.location.state?.action !== this.props.location.state?.action) {
            this.loadData();
        }
    }

    public render() {
        if (this.props.location.state?.action === "select_template") {
            return this.renderTemplateSelection();
        }

        return (
            <div className={"viewer-root full-height" + (this.state.uploadDragCounter > 0 ? " photo-drag" : "")} onDragEnter={this.handleDragEnter} onDragLeave={this.handleDragLeave} onDragOver={this.handleDragOver} onDrop={this.handleDragDrop}>
                <ViewToolbar>
                    <ToolbarMenuItem>
                        <button className="icon-upload" title="Upload document..." onClick={this.toggleUploadDialog} />
                    </ToolbarMenuItem>
                </ViewToolbar>
                <FileUploadDialog
                    isOpen={this.state.isUploadDialogOpen}
                    onRequestClose={this.toggleUploadDialog}
                    closeOnSuccess={true}
                    onUploadSuccess={this.uploadSuccess}
                    preventUpload={!this.state.uploadDocumentType}
                    accept="application/pdf,image/*,text/plain"
                    endpoint="document/upload"
                    filesOverride={this.state.uploadFilesOverride}
                    extraData={{
                        documentType: this.state.uploadDocumentType,
                        documentTypeVersion: this.state.allDocumentTypes.find(a => a.id === this.state.uploadDocumentType)?.latestVersion?.version
                    }}>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-document-type">Document type</label>
                            <select id="frm-document-type" name="uploadDocumentType" value={this.state.uploadDocumentType} onChange={this.handleInputChanged}>
                                <option value="" />
                                <optgroup label="Imported Documents">
                                    {this.state.allDocumentTypes.filter(a => a.importOnly).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </optgroup>
                                <optgroup label="Digital Forms">
                                    {this.state.allDocumentTypes.filter(a => !a.importOnly).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </optgroup>
                            </select>
                        </div>
                    </section>
                    <div>
                        <small>
                            <br />
                            If you want to import multiple documents that have barcodes, click <Link to={{ pathname: "/patient/document-import", state: { defaultUploadOpen: true } }}>here</Link>
                        </small>
                    </div>
                </FileUploadDialog>

                <h3>Individual Forms</h3>
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th className="col-smallest"><i className="icon-file-text-o" title="Has templates" /></th>
                            <th className="col-smallest">Version</th>
                            <th className="col-smallest">Date Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.isLoading === true
                            ? <tr>
                                <td colSpan={10}><div className="spinner spinner-center lds-dual-ring" /></td>
                            </tr>
                            : typeof this.state.isLoading === "string"
                                ? <tr>
                                    <td colSpan={10} className="error-text">{this.state.isLoading}</td>
                                </tr>
                                : this.state.documentTypes.map(a => <tr key={a.id} className="clickable" onClick={() => this.selectDocument(a)}>
                                    <td>
                                        <div>{a.name}</div>
                                        <small>{a.description}</small>
                                    </td>
                                    <td data-caption="Has templates: ">{a.latestVersion.hasTemplates ? <i className="icon-file-text-o" title="Has templates" /> : null}</td>
                                    <td data-caption="Version: ">{a.latestVersion.version}</td>
                                    <td>{formatDateLocalNumeric(a.latestVersion.creationTimestamp)}</td>
                                </tr>)}
                    </tbody>
                </table>
            </div>
        );
    }

    private renderTemplateSelection() {
        const canDeleteTemplates = this.props.authContext.hasRight("document:temp:new");

        return <div className="viewer-root">
            <DeletionConfirmationDialog
                isOpen={this.state.isDeleteDialogOpen}
                description={`Are you sure you want to delete '${this.state.deleteSelected?.name}'?`}
                onRequestClose={this.toggleDeleteDialog}
                onConfirm={this.deleteTemplate}
                onSuccess={() => this.loadTemplates(this.props.location.state?.data.id)} />

            <p>This document type has some pre-filled templates. You may select one, or start with an empty one.</p>
            <table className="table-vertical">
                <thead>
                    <tr>
                        <th colSpan={2}>Template</th>
                        {canDeleteTemplates
                            ? <th className="col-smallest" />
                            : null}
                    </tr>
                </thead>
                {this.state.isLoading === true
                    ? <tbody>
                        <tr>
                            <td colSpan={2}><div className="spinner spinner-center lds-dual-ring" /></td>
                        </tr>
                    </tbody>
                    : typeof this.state.isLoading === "string"
                        ? <tbody>
                            <tr>
                                <td colSpan={3}><div className="error-text">{this.state.isLoading}</div></td>
                            </tr>
                        </tbody>
                        : <tbody>
                            <tr className="clickable" onClick={() => this.selectDocument({ id: this.props.location.state?.data.id })}>
                                <td className="col-smallest"><i className="icon-plus" /></td>
                                <td>Empty document</td>
                                <td />
                            </tr>
                            {this.state.templates.map(a => <tr key={a.id} className="clickable" onClick={() => this.selectDocument({ id: this.props.location.state?.data.id }, a.id)}>
                                <td className="col-smallest"><i className="icon-file-text-o" /></td>
                                <td>{a.name}</td>
                                <td className="col-nopad-vt"><button className="bare red icon-times" title="Delete template" onClick={e => { e.stopPropagation(); this.prepareDeleteTemplate(a) }} /></td>
                            </tr>)}
                        </tbody>}
            </table>
        </div>;
    }

    private async loadData() {
        try {
            this.setState({
                isLoading: true
            });

            const [result/*, collectionsResult*/] = await Promise.all([
                V2.get<any>(`document/types?limit=100&show_all=true`),
                //V2.get<any>(`document/collections?limit=100`)
            ]);

            this.setState({
                isLoading: false,
                documentTypes: result.documentTypes.filter((a: any) => !a.importOnly),
                allDocumentTypes: result.documentTypes,
                //collections: collectionsResult.collections
            });

            await this.loadPatientId();
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load data"
            });
        }
    }

    private loadPatientId() {
        return new Promise<void>((resolve, reject) => {
            if (this.props.match.params.patient_id) {
                this.setState({
                    patientId: this.props.match.params.patient_id
                }, () => resolve());
                return;
            }

            if (this.props.match.params.surgery_id) {
                // Look up patient ID from surgery
                V2.get<any>(`surgery/${this.props.match.params.surgery_id}/patient`)
                    .then((info) => {
                        console.log(info);
                        this.setState({
                            patientId: info.patientId
                        }, () => resolve());
                    })
                return;
            }
        });
    }

    private async loadTemplates(typeId: string) {
        try {
            this.setState({
                isLoading: true
            });

            const templateResult = await V2.get<any>(`document/types/${typeId}/templates`);

            this.setState({
                isLoading: false,
                templates: templateResult.templates
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load templates"
            });
        }
    }

    private selectDocument(documentType: any, templateId?: string) {
        if (documentType.latestVersion?.hasTemplates) {
            // Send to template selection page
            this.props.history.push(this.props.location.pathname, { action: "select_template", data: { id: documentType.id } });

            return;
        }

        this.props.history.push(this.props.location.pathname + "/" + documentType.id, templateId ? { templateId } : undefined);
    }

    private toggleUploadDialog() {
        const { isUploadDialogOpen } = this.state;
        this.setState({
            isUploadDialogOpen: !isUploadDialogOpen
        });
    }

    private handleInputChanged(e: ChangeEvent<HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            hasChanged: true,
            [name]: value
        } as any)
    }

    private async uploadSuccess(ids: string[], responses?: any[]) {
        // Document is uploaded, attach to patient and/or surgery
        const documentId = responses![0].documentId;

        await V2.put<void>(`patients/${this.state.patientId}/documents/link/${documentId}`);
        if (this.props.match.params.surgery_id) {
            // Link document to surgery too
            await V2.put<void>(`surgery/${this.props.match.params.surgery_id}/documents/link/${documentId}`);
        }

        this.props.history.goBack();
    }

    private handleDragEnter(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        e.dataTransfer.effectAllowed = "copy";

        const uploadDragCounter = this.state.uploadDragCounter;

        this.setState({
            uploadDragCounter: uploadDragCounter + 1
        });
    }

    private handleDragLeave(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        e.dataTransfer.effectAllowed = "copy";

        const uploadDragCounter = this.state.uploadDragCounter;

        this.setState({
            uploadDragCounter: uploadDragCounter - 1
        });
    }

    private handleDragOver(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        e.dataTransfer.effectAllowed = "copy";
    }

    private handleDragDrop(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.persist();

        this.setState({
            uploadDragCounter: 0,
            uploadFilesOverride: e.dataTransfer.files.length > 0
                ? e.dataTransfer.files
                : undefined,
            isUploadDialogOpen: true
        });
    }

    private prepareDeleteTemplate(template: any) {
        this.setState({
            isDeleteDialogOpen: true,
            deleteSelected: template
        });
    }

    private toggleDeleteDialog() {
        const isDeleteDialogOpen = this.state.isDeleteDialogOpen;
        this.setState({
            isDeleteDialogOpen: !isDeleteDialogOpen
        });
    }

    private deleteTemplate() {
        if (!this.state.deleteSelected) {
            return Promise.reject({ message: "No template was selected" });
        }

        return V2.delete<void>(`document/types/${this.props.location.state?.data.id}/templates/${this.state.deleteSelected.id}`);
    }
}

export default withTitle("Select Document Template", withAuth(DocumentSelectionView));
